import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { queryKeys } from '@/api/queryKeys'
import type { components } from '@/../types/api'

/**
 * Engajamento pelo YouTube Studio (card #255): as abas Posts e Interações do
 * modal do canal no Scheduler. Ver apps/backend/docs/plan-studio-engagement.md.
 */
export type EngagementChannelOut = components['schemas']['EngagementChannelOut']
export type EngagementConfigOut = components['schemas']['EngagementConfigOut']
export type EngagementPatchIn = components['schemas']['EngagementPatchIn']
export type StudioSessionOut = components['schemas']['StudioSessionOut']
export type AutomationOut = components['schemas']['AutomationOut']
export type PostOut = components['schemas']['PostOut']
export type PostPreviewIn = components['schemas']['PostPreviewIn']
export type PostPreviewOut = components['schemas']['PostPreviewOut']
export type RecentActionOut = components['schemas']['RecentActionOut']
export type PinStateOut = components['schemas']['PinStateOut']
export type PinEligibilityOut = components['schemas']['PinEligibilityOut']
export type ChannelEngagementSummaryOut = components['schemas']['ChannelEngagementSummaryOut']

export function useChannelEngagement(slug: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.engagement.channel(slug),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/engagement/channels/{slug}', {
        params: { path: { slug } },
      })
      if (!data) throw new Error('engagement channel empty')
      return data.data
    },
    enabled: enabled && Boolean(slug),
    staleTime: 15_000,
  })
}

export function usePatchEngagement(slug: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: EngagementPatchIn) => {
      const { data } = await api.PATCH('/api/v1/engagement/channels/{slug}', {
        params: { path: { slug } },
        body: input,
      })
      if (!data) throw new Error('engagement patch empty')
      return data.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.engagement.all })
    },
  })
}

export function useEngagementPosts(slug: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.engagement.posts(slug),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/engagement/channels/{slug}/posts', {
        params: { path: { slug } },
      })
      if (!data) throw new Error('engagement posts empty')
      return data.data
    },
    enabled: enabled && Boolean(slug),
    staleTime: 15_000,
  })
}

/** Aprovar, Rejeitar e Editar valem na hora — não dependem do Salvar do modal. */
export function usePostAction(slug: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (
      input:
        | { action: 'approve' | 'reject'; postId: number }
        | { action: 'edit'; postId: number; text: string; options?: string[] | null },
    ) => {
      const path = { slug, post_id: input.postId }
      if (input.action !== 'edit') {
        const { data } =
          input.action === 'approve'
            ? await api.POST('/api/v1/engagement/channels/{slug}/posts/{post_id}/approve', { params: { path } })
            : await api.POST('/api/v1/engagement/channels/{slug}/posts/{post_id}/reject', { params: { path } })
        if (!data) throw new Error(`${input.action} empty`)
        return data.data
      }
      const { data } = await api.PATCH('/api/v1/engagement/channels/{slug}/posts/{post_id}', {
        params: { path },
        body: { text: input.text, options: input.options ?? null },
      })
      if (!data) throw new Error('edit empty')
      return data.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.engagement.posts(slug) })
      void qc.invalidateQueries({ queryKey: queryKeys.engagement.summary() })
    },
  })
}

/** Prévia com a config ainda não salva. Só usa IA; nada vai para a fila. */
export function usePreviewPosts(slug: string) {
  return useMutation({
    mutationFn: async (input: PostPreviewIn) => {
      const { data } = await api.POST('/api/v1/engagement/channels/{slug}/posts/preview', {
        params: { path: { slug } },
        body: input,
      })
      if (!data) throw new Error('preview empty')
      return data.data
    },
  })
}

/** "Verificar sessão": abre o Studio no servidor e confere o login (~20 s). */
export function useCheckStudioSession() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.POST('/api/v1/engagement/session/check')
      if (!data) throw new Error('session check empty')
      return data.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.engagement.all })
    },
  })
}

/**
 * "Conferir agora": relê a verificação do canal na Data API (1 unidade, um
 * clique por canal por minuto). Ver plan-studio-pin-eligibility-refresh.md.
 */
export function useRefreshPinEligibility(slug: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.POST('/api/v1/engagement/channels/{slug}/pin-eligibility/refresh', {
        params: { path: { slug } },
      })
      if (!data) throw new Error('pin eligibility refresh empty')
      return data.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.engagement.all })
    },
  })
}

/** Por canal: rascunhos esperando aprovação e o que está ligado (tabela do Scheduler). */
export function useEngagementSummary() {
  return useQuery({
    queryKey: queryKeys.engagement.summary(),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/engagement/summary')
      if (!data) throw new Error('engagement summary empty')
      return data.data
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
  })
}
